import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { App as AntdApp } from "antd";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";
import { toRGLLayout } from "features/analytics/studio/utils/boardLayoutUtils";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import BoardCanvas from "features/analytics/studio/components/board/BoardCanvas";

const EXECUTABLE_TYPES = new Set(["insights", "funnel", "flow", "retention"]);

export default function PublicBoardPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  // Context-aware, unlike the static `message` import: antd v5 renders static
  // calls through a detached root that never sees ConfigProvider, so those
  // toasts stayed light in dark mode.
  const { message } = AntdApp.useApp();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cardResults, setCardResults] = useState({});

  useEffect(() => {
    if (!token) {
      message.error("Invalid board link");
      navigate("/");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    analyticsApi
      .getPublicBoard(token)
      .then((response) => {
        if (cancelled) return;
        const data = response?.data;
        if (!data) {
          message.error("Board not found or expired");
          navigate("/");
          return;
        }
        setBoard(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error loading public board:", err);
        message.error("Failed to load board. It may have expired or been removed.");
        setError(err.message || "Failed to load board");
        navigate("/");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, navigate, message]);

  useEffect(() => {
    if (!board?.cards?.length || !token) return;
    board.cards.forEach((card) => {
      const type = (card?.cardType || "").toLowerCase();
      if (!EXECUTABLE_TYPES.has(type)) return;
      analyticsApi
        .executeCardPublic(token, card.id)
        .then((res) => {
          const result = res?.data != null ? res.data : res;
          setCardResults((prev) => ({ ...prev, [card.id]: result }));
        })
        .catch((err) => {
          setCardResults((prev) => ({
            ...prev,
            [card.id]: { error: err?.message || "Failed to load" },
          }));
        });
    });
  }, [board?.cards, token]);

  const gridCards = board?.cards || [];
  const rglLayout = useMemo(() => toRGLLayout(gridCards), [gridCards]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--surface-base)" }}>
        <div className="text-sm" style={{ color: theme.textMuted }}>Loading…</div>
      </div>
    );
  }

  if (error || !board) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--surface-base)" }}>
        <div className="text-sm" style={{ color: "var(--feedback-error)" }}>{error || "Board not found"}</div>
      </div>
    );
  }

  return (
    <div
      className="public-board-page"
      style={{
        backgroundColor: "var(--surface-base)",
        fontFamily: "Inter, sans-serif",
        minHeight: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "28px 40px 20px",
          maxWidth: 1200,
          margin: "0 auto",
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Shared report
        </p>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: theme.textPrimary, margin: 0, lineHeight: 1.3 }}>
          {board.name || "Untitled"}
        </h1>
        {board.description ? (
          <p style={{ fontSize: 14, color: theme.textSecondary, marginTop: 6, marginBottom: 0, lineHeight: 1.5 }}>
            {board.description}
          </p>
        ) : null}
      </div>

      {/* Canvas */}
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 40px 60px" }}>
        <BoardCanvas
          cards={gridCards}
          layout={rglLayout}
          cardResults={cardResults}
          readOnly
          boardId={board.id}
        />
      </main>
    </div>
  );
}
